import { useEffect, useState } from "react";
import { SlidersHorizontal, X, LayoutGrid, Map as MapIcon } from "lucide-react";
import MapView from "../components/MapView";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { LISTING_TYPES, CITIES, STRATEGIES, DISTRESS_TYPES, TIMELINES, IMAGES } from "../lib/constants";
import { PageHero, Disclaimer } from "../components/Common";
import { PropertyCard } from "../components/PropertyCard";
import { Reveal, PageFade } from "../components/Motion";

const Sel = ({ k, label, options, f, set }) => (
  <select className="field !py-3 text-sm" value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`filter-${k.replace(/_/g, "-")}`} aria-label={label}>
    <option value="">{label}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);
const Num = ({ k, label, f, set }) => (
  <input className="field !py-3 text-sm" inputMode="numeric" placeholder={label} value={f[k] || ""} onChange={(e) => set(k, e.target.value.replace(/[^0-9]/g, ""))} data-testid={`filter-${k.replace(/_/g, "-")}`} />
);

export function Marketplace({ category, cities }) {
  const [f, setF] = useState({});
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("grid");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    const params = Object.fromEntries(Object.entries({ ...f, category }).filter(([, v]) => v));
    const t = setTimeout(() => {
      api.get("/properties", { params }).then((r) => setItems(cities ? r.data.filter((p) => cities.includes(p.city)) : r.data)).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [f, category, cities]);

  const filters = (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <input className="field !py-3 text-sm lg:col-span-2" placeholder="Search by keyword" value={f.q || ""} onChange={(e) => set("q", e.target.value)} data-testid="filter-q" />
      <Sel k="property_type" label="Property type" options={LISTING_TYPES} f={f} set={set} />
      {!cities && <Sel k="city" label="Location" options={CITIES} f={f} set={set} />}
      <Num k="min_price" label="Min price ($)" f={f} set={set} />
      <Num k="max_price" label="Max price ($)" f={f} set={set} />
      <Num k="min_size" label="Min size (SF)" f={f} set={set} />
      <Num k="max_size" label="Max size (SF)" f={f} set={set} />
      <Sel k="strategy" label="Investment strategy" options={STRATEGIES} f={f} set={set} />
      {category === "distressed" && <Sel k="distress_type" label="Distress type" options={DISTRESS_TYPES} f={f} set={set} />}
      {category === "distressed" && <Sel k="closing_timeline" label="Closing timeline" options={TIMELINES} f={f} set={set} />}
      <button onClick={() => setF({})} className="text-sm text-mist hover:text-navy text-left px-1" data-testid="filter-reset">Reset filters</button>
    </div>
  );

  return (
    <section className="bg-paper py-12 sm:py-16" data-testid="marketplace">
      <div className="container-x">
        <div className="md:hidden flex items-center justify-between mb-4">
          <button onClick={() => setOpen(!open)} className="btn-ghost text-navy !py-2.5" data-testid="filter-toggle-mobile">
            {open ? <X className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />} Filters
          </button>
          <span className="text-sm text-mist" data-testid="results-count-mobile">{items?.length ?? "…"} results</span>
        </div>
        <div className={`${open ? "block" : "hidden"} md:block bg-white border border-navy/10 p-4 sm:p-5`}>{filters}</div>
        <div className="mt-6 flex items-center justify-between gap-4">
          <p className="hidden md:block text-sm text-mist" data-testid="results-count">{items == null ? "Loading…" : `${items.length} opportunit${items.length === 1 ? "y" : "ies"}`}</p>
          <div className="flex border border-navy/15 bg-white ml-auto" data-testid="view-toggle">
            {[["grid", LayoutGrid, "Grid"], ["map", MapIcon, "Map"]].map(([v, Icon, l]) => (
              <button key={v} onClick={() => setView(v)} className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${view === v ? "bg-navy text-paper" : "text-navy hover:bg-navy/5"}`} data-testid={`view-toggle-${v}`}>
                <Icon className="h-4 w-4" /> {l}
              </button>
            ))}
          </div>
        </div>
        {view === "map" && items ? (
          <div className="mt-6"><MapView items={items} /></div>
        ) : (
          <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items?.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 0.06}><PropertyCard p={p} /></Reveal>)}
          </div>
        )}
        {items?.length === 0 && (
          <div className="mt-6 border border-dashed border-navy/20 p-12 text-center text-mist" data-testid="no-results">
            No opportunities match these filters right now. Submit your criteria and we'll search for you.
          </div>
        )}
        <div className="mt-12"><Disclaimer testid="marketplace-disclaimer" /></div>
      </div>
    </section>
  );
}

export default function Opportunities() {
  useSeo("Oregon Commercial Investment Properties", "Browse Oregon commercial real estate investment opportunities sourced by Sido Holdings — office, retail, industrial, multifamily, land and value-add properties.");
  return (
    <PageFade>
      <PageHero eyebrow="Opportunities" title="Oregon Commercial Opportunities." image={IMAGES.facade}
        sub="A curated view of opportunities Sido Holdings is sourcing and evaluating. Sensitive details and documents are shared with qualified investors upon request." />
      <Marketplace />
    </PageFade>
  );
}
